import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Briefcase, Search, RefreshCw, Plus, Edit, Trash2, 
  User, Clock, AlertCircle, CheckCircle, X, 
  Download, Upload, Users, Building2, ChevronDown, ChevronRight, FolderPlus
} from 'lucide-react';
import { FilterButton } from '../../../components/UI/FilterButton';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import PillSelect from '../../../components/UI/PillSelect';
import DatePickerField from '../../../components/UI/DatePickerField';
import { 
  useProjectInvolvements,
  useCreateProjectInvolvement,
  useUpdateProjectInvolvement,
  useDeleteProjectInvolvement,
  useEmployeesWithoutProjectData
} from '../../../hooks/useCVData';
import { useProjects } from '../../../hooks/useConfigData';
import { useStaffList } from '../../../hooks/useStaffData';
import ConfirmModal from '../../../components/UI/ConfirmModal';
import { useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../../../services/api';
import { formatDate, formatDateTime, formatDateForInput, toLocalISODate, parseLocalDate } from '../../../utils/dateUtils';
import toast from '../../../utils/toast';
import Portal from '../../../components/UI/Portal';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import { Pagination } from '../../../components/UI/Pagination';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { InfoBadge } from '../../../components/UI/InfoBadge';

/**
 * Searchable Employee Dropdown - Groups employees by department with search functionality
 */
const SearchableEmployeeDropdown = ({ 
  staffList, 
  value, 
  onChange, 
  placeholder = "Select employee...",
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDepts, setExpandedDepts] = useState({});
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Group employees by department
  const groupedByDepartment = useMemo(() => {
    const groups = {};
    staffList.forEach(staff => {
      const dept = staff.department?.department_name || staff.department || 'Unassigned';
      if (!groups[dept]) {
        groups[dept] = [];
      }
      groups[dept].push(staff);
    });
    // Sort departments alphabetically, but put "Unassigned" at the end
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    return { groups, sortedKeys };
  }, [staffList]);

  // Filter employees based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) {
      return groupedByDepartment;
    }
    const term = searchTerm.toLowerCase();
    const filtered = {};
    Object.entries(groupedByDepartment.groups).forEach(([dept, employees]) => {
      const matchingEmployees = employees.filter(emp => 
        emp.full_name?.toLowerCase().includes(term) ||
        emp.staff_id?.toLowerCase().includes(term)
      );
      if (matchingEmployees.length > 0) {
        filtered[dept] = matchingEmployees;
      }
    });
    const sortedKeys = Object.keys(filtered).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    return { groups: filtered, sortedKeys };
  }, [groupedByDepartment, searchTerm]);

  // Get selected employee display name
  const selectedEmployee = staffList.find(s => s.staff_id === value);
  const displayValue = selectedEmployee 
    ? `${selectedEmployee.full_name} (${selectedEmployee.staff_id})`
    : placeholder;

  const toggleDepartment = (dept) => {
    setExpandedDepts(prev => ({ ...prev, [dept]: !prev[dept] }));
  };

  const handleSelect = (staffId) => {
    onChange(staffId);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Expand all departments when searching
  useEffect(() => {
    if (searchTerm) {
      const allExpanded = {};
      filteredGroups.sortedKeys.forEach(dept => {
        allExpanded[dept] = true;
      });
      setExpandedDepts(allExpanded);
    }
  }, [searchTerm, filteredGroups.sortedKeys]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`input-admin w-full text-left flex items-center justify-between ${
          isLoading 
            ? 'cursor-not-allowed opacity-60' 
            : 'cursor-pointer'
        } ${
          !value ? 'text-gray-400' : 'text-gray-900'
        }`}
      >
        <span className="truncate">
          {isLoading ? 'Loading employees...' : displayValue}
        </span>
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Search by name or staff ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Employee List */}
          <div className="overflow-y-auto max-h-60">
            {filteredGroups.sortedKeys.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No employees found
              </div>
            ) : (
              filteredGroups.sortedKeys.map(dept => (
                <div key={dept}>
                  {/* Department Header */}
                  <button
                    type="button"
                    onClick={() => toggleDepartment(dept)}
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {expandedDepts[dept] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">{dept}</span>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                      {filteredGroups.groups[dept].length}
                    </span>
                  </button>

                  {/* Employees in Department */}
                  {expandedDepts[dept] && (
                    <div className="bg-white">
                      {filteredGroups.groups[dept].map(emp => (
                        <button
                          key={emp.staff_id}
                          type="button"
                          onClick={() => handleSelect(emp.staff_id)}
                          className={`w-full px-4 pl-10 py-2 text-left text-sm hover:bg-red-50 flex items-center justify-between ${
                            value === emp.staff_id ? 'bg-red-50 text-red-700' : 'text-gray-700'
                          }`}
                        >
                          <span>{emp.full_name}</span>
                          <span className="text-xs text-gray-400">{emp.staff_id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Searchable Project Dropdown - With search and ability to add new project
 */
const SearchableProjectDropdown = ({ 
  projects, 
  value, 
  onChange, 
  onAddNew,
  placeholder = "Select project...",
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter projects based on search term
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) {
      return projects;
    }
    const term = searchTerm.toLowerCase();
    return projects.filter(proj => 
      proj.project_name?.toLowerCase().includes(term) ||
      proj.project_code?.toLowerCase().includes(term)
    );
  }, [projects, searchTerm]);

  // Get project identifier - Project model uses project_code as primary key
  const getProjectId = (proj) => proj.project_code || proj.id;

  // Get selected project display name
  const selectedProject = projects.find(p => getProjectId(p) === value);
  const displayValue = selectedProject 
    ? selectedProject.project_name
    : placeholder;

  const handleSelect = (projectId) => {
    onChange(projectId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleAddNew = () => {
    setIsOpen(false);
    onAddNew?.(searchTerm);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`input-admin w-full text-left flex items-center justify-between cursor-pointer ${
          !value ? 'text-gray-400' : 'text-gray-900'
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Project List */}
          <div className="overflow-y-auto max-h-52">
            {filteredProjects.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No projects found
              </div>
            ) : (
              filteredProjects.map(proj => {
                const projId = getProjectId(proj);
                return (
                  <button
                    key={projId}
                    type="button"
                    onClick={() => handleSelect(projId)}
                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 flex items-center justify-between ${
                      value === projId ? 'bg-red-50 text-red-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span>{proj.project_name}</span>
                    </div>
                    {proj.project_code && (
                      <span className="text-xs text-gray-400">{proj.project_code}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Add New Project Option */}
          {onAddNew && (
            <div className="border-t border-gray-100 p-2">
              <button
                type="button"
                onClick={handleAddNew}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 font-medium"
              >
                <FolderPlus className="w-4 h-4" />
                {searchTerm ? `Add "${searchTerm}" as new project` : 'Add new project'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Source badge for an involvement row — shared by desktop table and mobile cards. */
const renderSourceBadge = (involvement) => (
  <InfoBadge
    color={{
      clock_data: 'blue',
      external_activity: 'amber',
      manual: 'purple',
      hybrid: 'teal',
    }[involvement.involvement_source] || 'gray'}
    label={{
      clock_data: 'Clock Data',
      external_activity: 'External Activity',
      manual: 'Manual',
      hybrid: 'Hybrid',
    }[involvement.involvement_source] || involvement.involvement_source}
  />
);

/** Date range display for an involvement row — shared by desktop table and mobile cards. */
const renderDateRange = (involvement) =>
  involvement.date_range_display || (
    involvement.involvement_first_date && involvement.involvement_last_date ? (
      <>
        {formatDate(involvement.involvement_first_date)} - {formatDate(involvement.involvement_last_date)}
      </>
    ) : involvement.involvement_first_date ? (
      <>
        {formatDate(involvement.involvement_first_date)} - Present
      </>
    ) : '—'
  );

const ProjectInvolvementTab = () => {
  const queryClient = useQueryClient();
  
  // State
  const [filters, setFilters] = useState({
    staff_id: '',
    project_id: '',
    source: '',
    status: '',
    date_from: '',
    date_to: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingInvolvement, setEditingInvolvement] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectCode, setNewProjectCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  // Update current page when search query changes (debounced search handled in query params)
  useEffect(() => {
    if (searchQuery.trim()) {
      setCurrentPage(1); // Reset to first page on search
    }
  }, [searchQuery]);

  // Queries
  // Build query params - use 'search' for DRF SearchFilter, keep other filters separate
  const queryParams = {
    page: currentPage,
    page_size: pageSize,
    // Include search parameter if search query exists (DRF SearchFilter)
    ...(searchQuery.trim() && { search: searchQuery.trim() }),
    // Include other filters (excluding staff_id when using search, and empty values)
    ...Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => {
        // Exclude staff_id when search is active (search is more powerful)
        if (searchQuery.trim() && key === 'staff_id') return false;
        // Exclude empty values
        return value !== '' && value !== undefined;
      })
    ),
  };

  const { 
    data: involvements = { results: [], count: 0 }, 
    isLoading: involvementsLoading,
    refetch: refetchInvolvements
  } = useProjectInvolvements(queryParams);

  const { data: employeesWithoutData = [] } = useEmployeesWithoutProjectData();

  // Mutations
  const createInvolvement = useCreateProjectInvolvement();
  const updateInvolvement = useUpdateProjectInvolvement();
  const deleteInvolvement = useDeleteProjectInvolvement();

  // Form state
  const [formData, setFormData] = useState({
    involvement_staff_id: '',
    involvement_project: '',
    involvement_role_title: '',
    involvement_responsibilities: '',
    involvement_key_achievements: '',
    involvement_technologies: '',
    involvement_include_in_cv: true,
    involvement_cv_priority: 0,
    involvement_first_date: '',
    involvement_last_date: '',
  });

  // Get projects list using React Query hook (with page_size to fetch all projects)
  const { 
    data: projects = [], 
    isLoading: projectsLoading, 
    error: projectsError 
  } = useProjects({ page_size: 1000 });
  
  // Get staff list using React Query hook
  const { 
    data: staffData = { results: [] }, 
    isLoading: staffLoading 
  } = useStaffList({ page_size: 1000 });
  
  const staffList = staffData.results || staffData || [];

  // Helper function to calculate smart first date
  // Uses max(project_start_date, employment_start_date) - employee can't be involved before employment
  const calculateSmartFirstDate = useCallback((projectStartDate, employmentStartDate) => {
    if (!projectStartDate && !employmentStartDate) return null;
    if (!projectStartDate) return employmentStartDate;
    if (!employmentStartDate) return projectStartDate;
    
    // Use the later date (employee can't be involved before employment)
    const projectDate = new Date(projectStartDate);
    const employmentDate = new Date(employmentStartDate);
    return projectDate > employmentDate ? projectStartDate : employmentStartDate;
  }, []);

  // Error handling with toast notifications (following design system)
  useEffect(() => {
    if (projectsError) {
      toast.error('❌ Failed to load projects. Please refresh the page.');
    }
  }, [projectsError]);

  // Handlers
  const handleCreate = () => {
    setEditingInvolvement(null);
    setFormData({
      involvement_staff_id: '',
      involvement_project: '',
      involvement_role_title: '',
      involvement_responsibilities: '',
      involvement_key_achievements: '',
      involvement_technologies: '',
      involvement_include_in_cv: true,
      involvement_cv_priority: 0,
      involvement_first_date: '',
      involvement_last_date: '',
    });
    setShowForm(true);
  };

  const handleEdit = (involvement) => {
    setEditingInvolvement(involvement);
    
    // staff_id is the primary key for StaffUser, so use it directly
    // The API returns staff_id as a separate read-only field
    const staffIdForForm = involvement.staff_id || involvement.involvement_staff_id || '';
    
    // project_code is the primary key for Project
    const projectCodeForForm = involvement.project_code || involvement.involvement_project || '';
    
    setFormData({
      involvement_staff_id: staffIdForForm,
      involvement_project: projectCodeForForm,
      involvement_role_title: involvement.involvement_role_title || '',
      involvement_responsibilities: involvement.involvement_responsibilities || '',
      involvement_key_achievements: involvement.involvement_key_achievements || '',
      involvement_technologies: involvement.involvement_technologies || '',
      involvement_include_in_cv: involvement.involvement_include_in_cv !== false,
      involvement_cv_priority: involvement.involvement_cv_priority || 0,
      involvement_first_date: formatDateForInput(involvement.involvement_first_date) || '',
      involvement_last_date: formatDateForInput(involvement.involvement_last_date) || '',
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setConfirmAction({ id });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await deleteInvolvement.mutateAsync(confirmAction.id);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleSave = async () => {
    try {
      if (editingInvolvement) {
        // For updates, staff_id IS the primary key (no conversion needed)
        // involvement_staff_id expects the staff_id string directly
        // involvement_project expects the project_code string directly
        // Convert empty strings to null for date fields (backend expects null, not empty string)
        const updateData = {
          ...formData,
          involvement_first_date: formData.involvement_first_date || null,
          involvement_last_date: formData.involvement_last_date || null,
        };
        await updateInvolvement.mutateAsync({
          id: editingInvolvement.id,
          data: updateData,
        });
      } else {
        // Client-side duplicate check before submission
        const existingInvolvement = involvements.results?.find(
          inv => {
            const invStaffId = inv.involvement_staff_id?.staff_id || inv.staff_id || inv.involvement_staff_id;
            const invProjectId = inv.involvement_project?.project_code || inv.project_code || inv.involvement_project;
            const formStaffId = formData.involvement_staff_id;
            const formProjectId = formData.involvement_project;
            
            return invStaffId === formStaffId && invProjectId === formProjectId;
          }
        );
        
        if (existingInvolvement) {
          const projectName = existingInvolvement.project_name || existingInvolvement.project_code || 'this project';
          toast.error(
            `This employee already has project involvement for ${projectName}. ` +
            `Please edit the existing record instead.`
          );
          return;
        }
        
        // Transform form data to match backend CreateSerializer expectations
        const createData = {
          staff_id: formData.involvement_staff_id,
          project_id: formData.involvement_project,
          involvement_role_title: formData.involvement_role_title,
          involvement_responsibilities: formData.involvement_responsibilities,
          involvement_key_achievements: formData.involvement_key_achievements,
          involvement_technologies: formData.involvement_technologies,
          involvement_first_date: formData.involvement_first_date || null,
          involvement_last_date: formData.involvement_last_date || null,
          involvement_include_in_cv: formData.involvement_include_in_cv,
          involvement_cv_priority: formData.involvement_cv_priority,
        };
        await createInvolvement.mutateAsync(createData);
      }
      setShowForm(false);
      setEditingInvolvement(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleAddNewProject = (prefillName = '') => {
    setNewProjectName(prefillName);
    // Auto-generate a project code suggestion based on current date
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setNewProjectCode(`P${year}${month}-${random}`);
    setShowNewProjectModal(true);
  };

  const handleSaveNewProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('❌ Project name is required');
      return;
    }
    if (!newProjectCode.trim()) {
      toast.error('❌ Project code is required');
      return;
    }
    try {
      const response = await staffAPI.createProject({ 
        project_code: newProjectCode.trim(),
        project_name: newProjectName.trim(),
        project_type: 'other',
      });
      const newProject = response.data;
      
      // Invalidate projects cache to refetch with new project
      queryClient.invalidateQueries({ queryKey: ['config', 'projects'] });
      
      // Select the new project in the form - use project_code as the ID
      setFormData(prev => ({ ...prev, involvement_project: newProject.project_code }));
      setShowNewProjectModal(false);
      setNewProjectName('');
      setNewProjectCode('');
      toast.success('✅ Project created successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.project_code?.[0] || 
                          error.response?.data?.project_name?.[0] || 
                          error.response?.data?.error || 
                          'Failed to create project';
      toast.error(`❌ ${errorMessage}`);
    }
  };

  const clearFilters = () => {
    setFilters({
      staff_id: '',
      project_id: '',
      source: '',
      status: '',
      date_from: '',
      date_to: '',
    });
    setSearchQuery(''); // Clear search query when filters are cleared
    setCurrentPage(1);
  };

  const totalPages = Math.ceil((involvements.count || 0) / pageSize);

  return (
    <div className="space-y-6">
      {/* Project Involvement Table */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="p-6 border-b">
          {/* Header Row */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Project Involvement</h2>
          </div>
          
          {/* Toolbar Row */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Search Bar */}
              <DebouncedSearchInput
                value={searchQuery}
                onSearch={setSearchQuery}
                placeholder="Search by staff ID, name, project, or role..."
                className="max-w-xl"
              />
              
              {/* Filter Button */}
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={(filters.source ? 1 : 0) + (filters.status ? 1 : 0) + (filters.date_from ? 1 : 0) + (filters.date_to ? 1 : 0)}
                onClick={() => setShowFilters(!showFilters)}
              />
            </div>
            
            {/* Add Involvement - Pill-shaped button */}
            <button
              onClick={handleCreate}
              className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex items-center gap-2 flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Involvement</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>

          {/* Data Quality Indicators */}
          {employeesWithoutData.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <h3 className="text-sm font-semibold text-yellow-900">
                  {employeesWithoutData.length} employee(s) without project data
                </h3>
              </div>
              <p className="text-xs text-yellow-700">
                Consider syncing clock data or adding manual project involvement records.
              </p>
            </div>
          )}

        </div>

        {/* Filters */}
        {showFilters && (
          <div className="border-b p-4 bg-gray-50 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Source</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Sources' },
                    { value: 'clock_data', label: 'Clock Data' },
                    { value: 'external_activity', label: 'External Activity' },
                    { value: 'manual', label: 'Manual' },
                    { value: 'hybrid', label: 'Hybrid' },
                  ]}
                  value={filters.source}
                  onChange={(val) => setFilters(prev => ({ ...prev, source: val }))}
                  icon={Upload}
                  iconColor="text-red-600"
                  theme="admin"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  value={filters.status || ''}
                  onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
                  icon={CheckCircle}
                  iconColor="text-red-600"
                  theme="admin"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Date From</label>
                <DatePickerField
                  selected={parseLocalDate(filters.date_from)}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_from: date ? toLocalISODate(date) : '' }))}
                  className="input-admin pl-10 w-full"
                  placeholderText="Select date"
                  isClearable
                />
              </div>
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={clearFilters}
                className="btn btn-admin-outline btn-sm"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Loading / empty states — shared by both views */}
        {involvementsLoading ? (
          <div className="px-6 py-8 text-center">
            <LoadingSpinner size="md" />
          </div>
        ) : involvements.results?.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No project involvements found
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="md:hidden p-4 space-y-3">
              {involvements.results?.map((involvement) => {
                const projectName = involvement.project_name || involvement.involvement_project?.project_name;
                const projectCode = involvement.project_code || involvement.involvement_project?.project_code;
                return (
                  <div key={involvement.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {involvement.staff_name || involvement.involvement_staff_id?.full_name || involvement.involvement_staff_id || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {involvement.staff_id || involvement.involvement_staff_id?.staff_id || ''}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBadge status={involvement.involvement_status} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5 flex-1">
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Project</span>
                        <span className="text-gray-900 text-right truncate min-w-0">
                          {projectName || projectCode || 'N/A'}
                          {projectName && projectCode ? ` (${projectCode})` : ''}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Designation</span>
                        <span className="text-gray-900 text-right truncate min-w-0">{involvement.involvement_role_title || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Date Range</span>
                        <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{renderDateRange(involvement)}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Days</span>
                        <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{involvement.involvement_total_days || 0}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Hours</span>
                        <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{involvement.involvement_total_hours || 0}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Source</span>
                        <span className="text-right min-w-0">{renderSourceBadge(involvement)}</span>
                      </div>
                    </div>
                    <div className="card-action-footer justify-end gap-2">
                      <button
                        onClick={() => handleEdit(involvement)}
                        className="icon-button-admin"
                        title="Edit"
                        aria-label="Edit involvement"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(involvement.id)}
                        className="icon-button-danger"
                        title="Delete"
                        aria-label="Delete involvement"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Date Range</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Hours</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {involvements.results?.map((involvement) => (
                    <tr key={involvement.id} className="table-row">
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">
                          {involvement.staff_name || involvement.involvement_staff_id?.full_name || involvement.involvement_staff_id || 'N/A'}
                        </div>
                        {involvement.staff_id || involvement.involvement_staff_id?.staff_id ? (
                          <div className="text-xs text-gray-500 mt-1">
                            {involvement.staff_id || involvement.involvement_staff_id?.staff_id}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {involvement.project_name || involvement.involvement_project?.project_name ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {involvement.project_name || involvement.involvement_project?.project_name}
                            </div>
                            {involvement.project_code || involvement.involvement_project?.project_code ? (
                              <div className="text-xs text-gray-500 mt-1">
                                {involvement.project_code || involvement.involvement_project?.project_code}
                              </div>
                            ) : null}
                          </div>
                        ) : involvement.project_code || involvement.involvement_project?.project_code ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {involvement.project_code || involvement.involvement_project?.project_code}
                            </div>
                          </div>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {involvement.involvement_role_title || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {renderDateRange(involvement)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {involvement.involvement_total_days || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {involvement.involvement_total_hours || 0}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {renderSourceBadge(involvement)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <StatusBadge status={involvement.involvement_status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(involvement)}
                            className="icon-button-admin"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(involvement.id)}
                            className="icon-button-danger"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={involvements.count || 0}
          pageSize={pageSize}
          theme="admin"
          noun="results"
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <Portal>
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-container modal-container-md" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {editingInvolvement ? 'Edit Project Involvement' : 'Add Project Involvement'}
                </h3>
                <button onClick={() => setShowForm(false)} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="modal-content space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Employee *</label>
                  <SearchableEmployeeDropdown
                    staffList={staffList}
                    value={formData.involvement_staff_id}
                    onChange={(staffId) => {
                      // Find the selected employee
                      const selectedEmployee = staffList.find(s => s.staff_id === staffId);
                      // Auto-populate designation if field is empty
                      const updates = { involvement_staff_id: staffId };
                      if (selectedEmployee?.designation && !formData.involvement_role_title) {
                        updates.involvement_role_title = selectedEmployee.designation;
                      }
                      
                      // If project is already selected, recalculate first date with new employee
                      if (formData.involvement_project && selectedEmployee) {
                        const selectedProject = projects.find(p => 
                          (p.project_code || p.id) === formData.involvement_project
                        );
                        if (selectedProject) {
                          const smartFirstDate = calculateSmartFirstDate(
                            selectedProject.project_start_date,
                            selectedEmployee.employment_start_date
                          );
                          if (smartFirstDate) {
                            updates.involvement_first_date = formatDateForInput(smartFirstDate);
                          }
                        }
                      }
                      
                      setFormData(prev => ({ ...prev, ...updates }));
                    }}
                    placeholder="Select employee..."
                    isLoading={staffLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Project</label>
                  <SearchableProjectDropdown
                    projects={projects}
                    value={formData.involvement_project}
                    onChange={(projectId) => {
                      // Debug: Log project selection
                      console.log('[ProjectInvolvement] Project selected:', projectId);
                      console.log('[ProjectInvolvement] Available projects count:', projects.length);
                      
                      // Find selected project - try multiple lookup strategies
                      let selectedProject = projects.find(p => 
                        (p.project_code || p.id) === projectId
                      );
                      
                      // Fallback: try string comparison if first lookup fails
                      if (!selectedProject) {
                        selectedProject = projects.find(p => 
                          String(p.project_code || p.id) === String(projectId)
                        );
                      }
                      
                      // Debug: Log project lookup result
                      if (selectedProject) {
                        console.log('[ProjectInvolvement] Project found:', selectedProject.project_code || selectedProject.id);
                        console.log('[ProjectInvolvement] Project start date:', selectedProject.project_start_date);
                        console.log('[ProjectInvolvement] Project end date:', selectedProject.project_end_date);
                      } else {
                        console.warn('[ProjectInvolvement] Project not found for ID:', projectId);
                        console.log('[ProjectInvolvement] Available project codes:', projects.map(p => p.project_code || p.id).slice(0, 10));
                      }
                      
                      // Get currently selected employee (if any)
                      const selectedEmployee = formData.involvement_staff_id 
                        ? staffList.find(s => s.staff_id === formData.involvement_staff_id)
                        : null;
                      
                      // Prepare updates
                      const updates = { involvement_project: projectId };
                      
                      if (selectedProject) {
                        // Calculate smart first date (max of project_start_date and employment_start_date)
                        const projectStartDate = selectedProject.project_start_date;
                        const employmentStartDate = selectedEmployee?.employment_start_date;
                        
                        console.log('[ProjectInvolvement] Project start date:', projectStartDate);
                        console.log('[ProjectInvolvement] Employee employment start date:', employmentStartDate);
                        
                        const smartFirstDate = calculateSmartFirstDate(projectStartDate, employmentStartDate);
                        
                        console.log('[ProjectInvolvement] Calculated smart first date:', smartFirstDate);
                        
                        // Populate dates
                        if (smartFirstDate) {
                          const formattedFirstDate = formatDateForInput(smartFirstDate);
                          console.log('[ProjectInvolvement] Formatted first date:', formattedFirstDate);
                          updates.involvement_first_date = formattedFirstDate;
                        } else {
                          console.log('[ProjectInvolvement] No smart first date calculated');
                        }
                        
                        if (selectedProject.project_end_date) {
                          const formattedLastDate = formatDateForInput(selectedProject.project_end_date);
                          console.log('[ProjectInvolvement] Formatted last date:', formattedLastDate);
                          updates.involvement_last_date = formattedLastDate;
                        } else {
                          console.log('[ProjectInvolvement] No project end date available');
                        }
                      }
                      
                      console.log('[ProjectInvolvement] Final updates:', updates);
                      setFormData(prev => ({ ...prev, ...updates }));
                    }}
                    onAddNew={handleAddNewProject}
                    placeholder="Select project..."
                    isLoading={projectsLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Designation</label>
                  <input
                    type="text"
                    className="input-admin w-full"
                    placeholder="e.g., Site Supervisor, BIM Coordinator"
                    value={formData.involvement_role_title}
                    onChange={(e) => setFormData(prev => ({ ...prev, involvement_role_title: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Responsibilities</label>
                  <textarea
                    className="textarea-admin w-full rounded-xl"
                    rows={3}
                    placeholder="Key responsibilities and duties..."
                    value={formData.involvement_responsibilities}
                    onChange={(e) => setFormData(prev => ({ ...prev, involvement_responsibilities: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Key Achievements</label>
                  <textarea
                    className="textarea-admin w-full rounded-xl"
                    rows={2}
                    placeholder="Notable achievements or contributions..."
                    value={formData.involvement_key_achievements}
                    onChange={(e) => setFormData(prev => ({ ...prev, involvement_key_achievements: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Technologies</label>
                  <input
                    type="text"
                    className="input-admin w-full"
                    placeholder="Comma-separated list of technologies, tools, or methodologies"
                    value={formData.involvement_technologies}
                    onChange={(e) => setFormData(prev => ({ ...prev, involvement_technologies: e.target.value }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">First Date</label>
                    <DatePickerField
                      selected={parseLocalDate(formData.involvement_first_date)}
                      onChange={(date) => setFormData(prev => ({ ...prev, involvement_first_date: date ? toLocalISODate(date) : '' }))}
                      className="input-admin pl-10 w-full"
                      placeholderText="Select date"
                      isClearable
                      maxDate={parseLocalDate(formData.involvement_last_date)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Last Date</label>
                    <DatePickerField
                      selected={parseLocalDate(formData.involvement_last_date)}
                      onChange={(date) => setFormData(prev => ({ ...prev, involvement_last_date: date ? toLocalISODate(date) : '' }))}
                      className="input-admin pl-10 w-full"
                      placeholderText="Select date"
                      isClearable
                      minDate={parseLocalDate(formData.involvement_first_date)}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus-visible:ring-red-500"
                      checked={formData.involvement_include_in_cv}
                      onChange={(e) => setFormData(prev => ({ ...prev, involvement_include_in_cv: e.target.checked }))}
                    />
                    <span className="text-sm text-gray-700">Include in CV</span>
                  </label>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700">CV Priority</label>
                    <input
                      type="number"
                      className="input-admin w-full"
                      min="0"
                      value={formData.involvement_cv_priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, involvement_cv_priority: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowForm(false)} className="btn btn-admin-outline">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formData.involvement_staff_id || createInvolvement.isPending || updateInvolvement.isPending}
                  className="btn btn-admin-primary"
                >
                  {(createInvolvement.isPending || updateInvolvement.isPending) ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <Portal>
          <div className="modal-overlay" onClick={() => setShowNewProjectModal(false)}>
            <div className="modal-container modal-container-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Add New Project</h3>
                <button onClick={() => setShowNewProjectModal(false)} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="modal-content space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Project Code *</label>
                  <input
                    type="text"
                    className="input-admin w-full"
                    placeholder="e.g., P2401-001"
                    value={newProjectCode}
                    onChange={(e) => setNewProjectCode(e.target.value.toUpperCase())}
                  />
                  <p className="text-xs text-gray-400">Unique identifier for the project</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Project Name *</label>
                  <input
                    type="text"
                    className="input-admin w-full"
                    placeholder="Enter project name..."
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveNewProject();
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  This will create a basic project entry. You can add more details later in the Projects management section.
                </p>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowNewProjectModal(false)} className="btn btn-admin-outline">
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewProject}
                  disabled={!newProjectName.trim() || !newProjectCode.trim()}
                  className="btn btn-admin-primary"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Project Involvement"
        message="Are you sure you want to delete this project involvement?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="admin"
      />
    </div>
  );
};

export default ProjectInvolvementTab;

